import React, { useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Loader2, CheckCircle2, AlertCircle, FileText, Link as LinkIcon } from "lucide-react";
import { useCMAQuery, useCMACompsQuery, useCMADocumentsQuery } from "@/hooks/useCMAData";
import { useDefaultLogo } from "@/hooks/useDefaultLogo";
import { CMAMap } from "@/components/CMAMap";
import { ZillowIcon } from "@/components/icons/ZillowIcon";
import { CMANeighborhoodProfile } from "@/components/CMANeighborhoodProfile";
import { CMASellerFeedback } from "@/components/CMASellerFeedback";
import { CMANetSheetCalculator } from "@/components/CMANetSheetCalculator";
import NotFound from "@/pages/NotFound";

const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function formatPrice(n: number) {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(0)}K`;
  return `$${n.toLocaleString()}`;
}

function statusLabel(status: string | null) {
  if (!status) return "Unknown";
  if (status === "FOR_SALE") return "Active";
  if (status === "PENDING") return "Pending";
  if (status === "SOLD" || status.includes("SOLD")) return "Sold";
  return status;
}

function median(values: number[]): number | null {
  if (!values.length) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) / 2 : sorted[mid];
}

/** Upgrade a Zillow static CDN URL to the largest known size variant.
 *  Zillow CDN URL format: https://photos.zillowstatic.com/fp/{hash}-{transform}.{ext}
 *  e.g. abc123-cc_ft_960.webp → abc123-uncropped_scaled_within_1536_1152.webp */
function upgradeZillowImage(url: string | null | undefined): string | null {
  if (!url) return null;
  if (url.includes("zillowstatic.com")) {
    return url.replace(/-cc_ft_\d+(\.\w+)$/, "-uncropped_scaled_within_1536_1152$1");
  }
  return url;
}

/** Pick the best available image URL from Zillow property_data. */
function getBestPropertyImage(pd: Record<string, any>): string | null {
  // 1. Dedicated HD field (present on newer API responses)
  if (pd.desktopWebHdpImageLink) return pd.desktopWebHdpImageLink;
  if (pd.hiResImageLink) return pd.hiResImageLink;

  // 2. First photo from the photos array (actual MLS/listing photos, not street view)
  const photoUrl =
    pd.photos?.[0]?.mixedSources?.jpeg?.slice(-1)?.[0]?.url ||   // largest JPEG variant
    pd.photos?.[0]?.url ||
    pd.originalPhotos?.[0]?.mixedSources?.jpeg?.slice(-1)?.[0]?.url ||
    pd.originalPhotos?.[0]?.url ||
    null;
  if (photoUrl) return photoUrl;

  // 3. Upgrade the thumbnail URL to Zillow's uncropped 1536px variant
  return upgradeZillowImage(pd.imgSrc);
}

// Stat card matching Spotlight's rounded-2xl white/60 backdrop style
function StatCard({ value, label, accent }: { value: string; label: string; accent: string }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white/60 backdrop-blur-sm p-6 text-center shadow-sm">
      <p className="text-3xl md:text-4xl font-bold text-slate-900">{value}</p>
      <p className="text-sm text-slate-400 mt-2">{label}</p>
    </div>
  );
}

// Section wrapper matching Spotlight label style
function OpenAILogo({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
      <path d="M22.282 9.821a5.985 5.985 0 0 0-.516-4.911 6.046 6.046 0 0 0-6.51-2.9A6.065 6.065 0 0 0 4.981 4.182a5.985 5.985 0 0 0-3.998 2.9 6.046 6.046 0 0 0 .743 7.097 5.98 5.98 0 0 0 .511 4.911 6.051 6.051 0 0 0 6.515 2.9A5.985 5.985 0 0 0 13.26 24a6.056 6.056 0 0 0 5.772-4.206 5.99 5.99 0 0 0 3.997-2.9 6.056 6.056 0 0 0-.747-7.073zM13.26 22.43a4.476 4.476 0 0 1-2.876-1.041l.141-.081 4.779-2.758a.795.795 0 0 0 .392-.681v-6.737l2.02 1.168a.071.071 0 0 1 .038.052v5.583a4.504 4.504 0 0 1-4.494 4.494zM3.6 18.304a4.47 4.47 0 0 1-.535-3.014l.142.085 4.783 2.759a.771.771 0 0 0 .78 0l5.843-3.369v2.332a.08.08 0 0 1-.033.062L9.74 19.95A4.5 4.5 0 0 1 3.6 18.304zm-1.26-10.43A4.485 4.485 0 0 1 4.704 5.9l.001 5.7a.766.766 0 0 0 .388.677l5.814 3.354-2.02 1.168a.076.076 0 0 1-.071 0l-4.83-2.787A4.504 4.504 0 0 1 2.34 7.874zm16.597 3.855-5.785-3.339 2.015-1.163a.076.076 0 0 1 .071 0l4.83 2.791a4.494 4.494 0 0 1-.676 8.104v-5.677a.79.79 0 0 0-.407-.667l-.048-.049zm2.01-3.023-.141-.085-4.774-2.782a.776.776 0 0 0-.785 0L9.41 9.228V6.896a.066.066 0 0 1 .028-.061l4.83-2.787a4.5 4.5 0 0 1 6.68 4.66zm-12.64 4.135-2.02-1.164a.08.08 0 0 1-.038-.057V6.037a4.5 4.5 0 0 1 7.376-3.453l-.142.08L8.704 5.46a.795.795 0 0 0-.393.681zm1.097-2.365 2.602-1.5 2.607 1.5v2.999l-2.597 1.5-2.607-1.5z"/>
    </svg>
  );
}

function Section({ title, children, className = "" }: { title?: string; children: React.ReactNode; className?: string }) {
  return (
    <div className={`mb-10 ${className}`}>
      {title && (
        <p className="text-sm font-semibold uppercase tracking-[0.2em] text-slate-400 mb-6">
          {title}
        </p>
      )}
      {children}
    </div>
  );
}

export default function PublicCMA() {
  const { id } = useParams<{ id: string }>();
  const defaultLogo = useDefaultLogo();

  const isValidId = id && uuidRegex.test(id);
  const { data: cma, isLoading } = useCMAQuery(isValidId ? id : undefined);
  const { data: comps = [] } = useCMACompsQuery(isValidId ? id : undefined);
  const { data: documents = [] } = useCMADocumentsQuery(isValidId ? id : undefined);

  useEffect(() => {
    if (cma?.id && cma.is_published) {
      import("@/integrations/supabase/client").then(({ supabase }) => {
        supabase
          .from("cmas")
          .update({ public_url_views_count: (cma.public_url_views_count || 0) + 1 })
          .eq("id", cma.id)
          .then(() => {});
      });
    }
  }, [cma?.id]);

  const visibleComps = useMemo(() => comps.filter((c) => c.visible), [comps]);

  const marketStats = useMemo(() => {
    // Use all comps (visible + hidden) for broader market picture
    const soldComps = comps.filter(
      (c) => c.status === "SOLD" || c.status?.includes("SOLD")
    );
    const activeComps = comps.filter(
      (c) => c.status === "FOR_SALE" || c.status === "PENDING"
    );
    const prices = comps.map((c) => c.price).filter((p): p is number => p != null);
    const ppsfValues = comps.map((c) => c.price_per_sqft).filter((p): p is number => p != null);
    return {
      medianPrice: median(prices),
      medianPpsf: median(ppsfValues),
      soldCount: soldComps.length,
      activeCount: activeComps.length,
    };
  }, [comps]);

  if (!isValidId) return <NotFound />;
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#f8fafc]">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }
  if (!cma || !cma.is_published) return <NotFound />;

  const primaryColor = cma.primary_brand_color || "#0c0f24";
  const agentName = cma.custom_agent_name || "Your Agent";
  const logoUrl = cma.custom_logo_url || defaultLogo.defaultLogoUrl;
  const headshotUrl = cma.custom_agent_headshot_url;

  const pd = cma.property_data || {};
  const beds = pd.bedrooms;
  const baths = pd.bathrooms;
  const sqft = pd.livingArea || pd.squareFeet;
  const yearBuilt = pd.yearBuilt;
  // Agent-uploaded photo takes priority over Zillow imagery
  const imageUrl = cma.custom_property_image_url || getBestPropertyImage(pd);

  const structured = cma.ai_analysis_structured;
  // Read DOM from dedicated column (post-migration) or property_data fallback (pre-migration)
  const zipDom: number | null = cma.zip_sold_dom_median ?? cma.property_data?.zip_sold_dom_median ?? null;
  const hasMarketStats =
    comps.length > 0 &&
    (marketStats.medianPrice != null || marketStats.medianPpsf != null || zipDom != null);

  return (
    <div className="min-h-screen bg-[#f8fafc]">

      {/* ── HERO ── full-viewport with gradient overlay, text at bottom */}
      <section className="relative min-h-screen flex flex-col overflow-hidden">
        {/* Background image or solid color */}
        {imageUrl ? (
          <img
            src={imageUrl}
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
            onError={(e) => {
              // If upgraded URL fails, fall back to the raw thumbnail
              const fallback = pd.imgSrc;
              if (fallback && (e.target as HTMLImageElement).src !== fallback) {
                (e.target as HTMLImageElement).src = fallback;
              }
            }}
          />
        ) : (
          <div className="absolute inset-0" style={{ backgroundColor: primaryColor }} />
        )}

        {/* Gradient overlay — bottom-heavy like Spotlight */}
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(to top, rgba(0,0,0,0.88) 0%, rgba(0,0,0,0.55) 35%, rgba(0,0,0,0.15) 65%, transparent 100%)",
          }}
        />

        {/* Top bar: logo + prepared for */}
        <div className="relative z-10 flex items-center justify-between px-6 pt-6">
          {logoUrl ? (
            <img src={logoUrl} alt="Logo" className="h-10 md:h-12 w-auto max-w-[180px] object-contain drop-shadow-md" />
          ) : (
            <div />
          )}
          {cma.seller_name && (
            <div className="bg-black/35 backdrop-blur-sm border border-white/15 rounded-full px-4 py-1.5">
              <p className="text-xs text-white/70 leading-none">
                Prepared for{" "}
                <span className="font-semibold text-white">{cma.seller_name}</span>
              </p>
            </div>
          )}
        </div>

        {/* Address block — pinned to bottom of hero */}
        <div className="relative z-10 mt-auto px-6 pb-16 md:px-12 md:pb-24">
          {/* Report type label */}
          <div className="flex items-center gap-3 mb-3">
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-white/50">
              Comparative Market Analysis
            </p>
            <span className="flex items-center gap-1.5 text-xs font-medium text-white/60 bg-white/10 backdrop-blur-sm border border-white/15 rounded-full px-2.5 py-1">
              <OpenAILogo className="h-3 w-3" />
              AI-Powered
            </span>
          </div>
          {/* Brand accent bar */}
          <div
            className="w-16 h-1 rounded-full mb-4"
            style={{ backgroundColor: primaryColor }}
          />
          <h1 className="text-3xl md:text-5xl font-bold text-white leading-tight tracking-tight">
            {cma.address}
          </h1>
          <p className="text-white/70 text-lg mt-1">
            {[cma.city, cma.state, cma.zip].filter(Boolean).join(", ")}
          </p>

          {(beds || baths || sqft || yearBuilt) && (
            <div className="flex flex-wrap gap-2 mt-4">
              {beds && (
                <span className="bg-white/15 backdrop-blur-sm text-white text-xs font-medium px-3 py-1 rounded-full border border-white/20">
                  {beds} bed{beds !== 1 ? "s" : ""}
                </span>
              )}
              {baths && (
                <span className="bg-white/15 backdrop-blur-sm text-white text-xs font-medium px-3 py-1 rounded-full border border-white/20">
                  {baths} bath{baths !== 1 ? "s" : ""}
                </span>
              )}
              {sqft && (
                <span className="bg-white/15 backdrop-blur-sm text-white text-xs font-medium px-3 py-1 rounded-full border border-white/20">
                  {sqft.toLocaleString()} sqft
                </span>
              )}
              {yearBuilt && (
                <span className="bg-white/15 backdrop-blur-sm text-white text-xs font-medium px-3 py-1 rounded-full border border-white/20">
                  Built {yearBuilt}
                </span>
              )}
            </div>
          )}
        </div>
      </section>

      {/* ── MAIN CONTENT ── */}
      <div className="max-w-4xl mx-auto px-4 md:px-6 py-12">

        {/* AGENT RECOMMENDED PRICE */}
        {(cma.agent_price_low || cma.agent_price_high) && (
          <section
            className="rounded-2xl p-8 md:p-12 text-center mb-6"
            style={{ backgroundColor: primaryColor }}
          >
            <p className="text-sm font-semibold uppercase tracking-[0.2em] text-white/60 mb-4">
              Agent Recommended List Price
            </p>
            <p className="text-5xl md:text-6xl font-bold tracking-tight text-white">
              {cma.agent_price_low && cma.agent_price_high
                ? `${formatPrice(cma.agent_price_low)} – ${formatPrice(cma.agent_price_high)}`
                : cma.agent_price_low
                ? `From ${formatPrice(cma.agent_price_low)}`
                : `Up to ${formatPrice(cma.agent_price_high!)}`}
            </p>
            {agentName && (
              <p className="text-sm text-white/60 mt-4">Recommended by {agentName}</p>
            )}
          </section>
        )}

        {/* AI SUGGESTED LIST PRICE + ZESTIMATE */}
        {((cma.suggested_price_low || cma.suggested_price_high) || cma.price) && (
          <div className={`grid gap-6 mb-10 items-stretch ${(cma.suggested_price_low || cma.suggested_price_high) && cma.price ? "md:grid-cols-2" : "grid-cols-1"}`}>

            {(cma.suggested_price_low || cma.suggested_price_high) && (
              <section
                className="rounded-2xl border shadow-sm p-8 text-center flex flex-col justify-center"
                style={{
                  borderColor: primaryColor + "30",
                  background: `linear-gradient(135deg, ${primaryColor}08 0%, ${primaryColor}04 100%)`,
                }}
              >
                <div className="flex items-center justify-center gap-2 mb-4">
                  <p className="text-sm font-semibold uppercase tracking-[0.2em] text-slate-400">
                    AI Suggested List Price
                  </p>
                  <span className="flex items-center gap-1.5 text-xs font-medium text-slate-400 border border-slate-200 rounded-full px-2.5 py-1 bg-white">
                    <OpenAILogo className="h-3 w-3" />
                    OpenAI
                  </span>
                </div>
                <p
                  className="text-4xl md:text-5xl font-bold tracking-tight"
                  style={{ color: primaryColor }}
                >
                  {cma.suggested_price_low && cma.suggested_price_high
                    ? `${formatPrice(cma.suggested_price_low)} – ${formatPrice(cma.suggested_price_high)}`
                    : cma.suggested_price_low
                    ? `From ${formatPrice(cma.suggested_price_low)}`
                    : `Up to ${formatPrice(cma.suggested_price_high!)}`}
                </p>
                <p className="text-sm text-slate-400 mt-4">
                  Based on {visibleComps.length} comparable{" "}
                  {visibleComps.length === 1 ? "property" : "properties"}
                </p>
              </section>
            )}

            {cma.price && (
              <div className="rounded-2xl border border-[#006AFF]/20 shadow-sm p-8 text-center flex flex-col justify-center" style={{ background: "rgba(0,106,255,0.05)" }}>
                <div className="flex items-center justify-center gap-2 mb-4">
                  <ZillowIcon className="h-5 w-5" />
                  <span className="text-sm font-semibold uppercase tracking-[0.2em] text-[#006AFF]/60">Zestimate®</span>
                </div>
                <p className="text-4xl md:text-5xl font-bold text-slate-900">
                  ${cma.price.toLocaleString()}
                </p>
                <p className="text-sm text-slate-400 mt-4">estimated market value</p>
              </div>
            )}

          </div>
        )}

        {/* NET SHEET CALCULATOR */}
        {(cma.net_sheet_commission_pct != null || cma.net_sheet_mortgage_payoff != null) && (
          <Section title="Your Estimated Net Proceeds">
            <CMANetSheetCalculator
              agentPriceLow={cma.agent_price_low}
              agentPriceHigh={cma.agent_price_high}
              suggestedPriceLow={cma.suggested_price_low}
              suggestedPriceHigh={cma.suggested_price_high}
              mortgagePayoff={cma.net_sheet_mortgage_payoff}
              commissionPct={cma.net_sheet_commission_pct}
              closingCostPct={cma.net_sheet_closing_cost_pct}
              sellerCredits={cma.net_sheet_seller_credits}
              otherFees={cma.net_sheet_other_fees}
              primaryColor={primaryColor}
            />
          </Section>
        )}

        {/* SELLER PRICING FEEDBACK */}
        <Section>
          <CMASellerFeedback
            cmaId={cma.id}
            initialSentiment={cma.seller_feedback_sentiment}
            primaryColor={primaryColor}
          />
        </Section>

        {/* MARKET OVERVIEW */}
        {hasMarketStats && (
          <Section title={`Market Overview · ${comps.length} ${comps.length === 1 ? "Property" : "Properties"} Analyzed in the past 180 days`}>
            <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
              <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-y md:divide-y-0 divide-slate-100">
                {marketStats.medianPrice != null && (
                  <div className="p-6 md:p-8 text-center">
                    <p className="text-3xl md:text-4xl font-bold text-slate-900">{formatPrice(marketStats.medianPrice)}</p>
                    <p className="text-sm text-slate-400 mt-2">Median Sale Price</p>
                  </div>
                )}
                {marketStats.medianPpsf != null && (
                  <div className="p-6 md:p-8 text-center">
                    <p className="text-3xl md:text-4xl font-bold text-slate-900">${Math.round(marketStats.medianPpsf)}</p>
                    <p className="text-sm text-slate-400 mt-2">Median $/sqft</p>
                  </div>
                )}
                <div className="p-6 md:p-8 text-center">
                  <p className="text-3xl md:text-4xl font-bold text-slate-900">
                    {zipDom ?? "—"}
                  </p>
                  <p className="text-sm text-slate-400 mt-2">Median Days on Market</p>
                </div>
                {(marketStats.soldCount > 0 || marketStats.activeCount > 0) && (
                  <div className="p-6 md:p-8 text-center">
                    <p className="text-3xl md:text-4xl font-bold text-slate-900">{marketStats.soldCount} / {marketStats.activeCount}</p>
                    <p className="text-sm text-slate-400 mt-2">Sold / Active</p>
                  </div>
                )}
              </div>
            </div>
          </Section>
        )}

        {/* NEIGHBORHOOD ACTIVITY */}
        {cma.neighborhood_data?.metrics && (
          <Section title="Neighborhood Activity">
            <CMANeighborhoodProfile
              neighborhoodData={cma.neighborhood_data}
              primaryColor={primaryColor}
              updatedAt={cma.neighborhood_data_updated_at}
            />
          </Section>
        )}

        {/* MARKET SUMMARY */}
        {structured?.marketSummary && (
          <Section title="Market Summary">
            <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-6 md:p-8">
              <p className="text-slate-700 leading-relaxed">{structured.marketSummary}</p>
            </div>
          </Section>
        )}

        {/* COMPARABLE PROPERTIES */}
        {visibleComps.length > 0 && (
          <Section title="Comparable Properties">
            <div className="grid gap-4 sm:grid-cols-2">
              {visibleComps.map((comp) => (
                <div
                  key={comp.id}
                  className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden"
                >
                  {comp.image_url && (
                    <div className="h-44 overflow-hidden">
                      <img
                        src={comp.image_url}
                        alt={comp.address || "Property"}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                  <div className="p-5">
                    <div className="flex items-start justify-between gap-2 mb-3">
                      <div className="min-w-0 flex-1">
                        <p className="font-semibold text-slate-900 truncate">{comp.address}</p>
                        <p className="text-sm text-slate-400">
                          {[comp.city, comp.state, comp.zip].filter(Boolean).join(", ")}
                        </p>
                      </div>
                      <Badge
                        className={
                          comp.status === "SOLD" || comp.status?.includes("SOLD")
                            ? "bg-slate-500 text-white text-xs flex-shrink-0"
                            : comp.status === "PENDING"
                            ? "bg-amber-500 text-white text-xs flex-shrink-0"
                            : "bg-emerald-600 text-white text-xs flex-shrink-0"
                        }
                      >
                        {statusLabel(comp.status)}
                      </Badge>
                    </div>
                    <p className="text-2xl font-bold" style={{ color: primaryColor }}>
                      {comp.price ? `$${comp.price.toLocaleString()}` : "Price N/A"}
                    </p>
                    <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2 text-sm text-slate-400">
                      {comp.bedrooms && <span>{comp.bedrooms} bd</span>}
                      {comp.bathrooms && <span>{comp.bathrooms} ba</span>}
                      {comp.square_feet && <span>{comp.square_feet.toLocaleString()} sqft</span>}
                      {comp.price_per_sqft && <span>${comp.price_per_sqft}/sqft</span>}
                      {comp.days_on_market && <span>{comp.days_on_market} DOM</span>}
                    </div>
                    {comp.zillow_url && (
                      <a
                        href={comp.zillow_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs mt-3 inline-block text-slate-400 hover:text-slate-600 hover:underline"
                      >
                        View on Zillow →
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </Section>
        )}

        {/* COMP MAP */}
        <Section title="Comp Map">
          <CMAMap
            subjectAddress={[cma.address, cma.city, cma.state, cma.zip].filter(Boolean).join(", ")}
            subjectLat={pd.latitude}
            subjectLng={pd.longitude}
            comps={visibleComps}
            primaryColor={primaryColor}
          />
        </Section>

        {/* PRICING ANALYSIS */}
        {structured?.pricingAnalysis && (
          <Section title="Pricing Analysis">
            <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-6 md:p-8">
              <p className="text-slate-700 leading-relaxed">{structured.pricingAnalysis}</p>
            </div>
          </Section>
        )}

        {/* KEY SELLING POINTS + COMPETITIVE FACTORS */}
        {(structured?.keySellingPoints?.length > 0 || structured?.competitiveFactors?.length > 0) && (
          <Section title="Property Details">
            <div className="grid md:grid-cols-2 gap-4">
              {structured?.keySellingPoints?.length > 0 && (
                <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-6">
                  <p className="text-sm font-semibold uppercase tracking-[0.15em] text-slate-400 mb-4">
                    Key Selling Points
                  </p>
                  <ul className="space-y-3">
                    {structured.keySellingPoints.map((point: string, i: number) => (
                      <li key={i} className="flex items-start gap-3 text-sm text-slate-700">
                        <CheckCircle2 className="h-4 w-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                        <span>{point}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {structured?.competitiveFactors?.length > 0 && (
                <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-6">
                  <p className="text-sm font-semibold uppercase tracking-[0.15em] text-slate-400 mb-4">
                    Competitive Factors
                  </p>
                  <ul className="space-y-3">
                    {structured.competitiveFactors.map((factor: string, i: number) => (
                      <li key={i} className="flex items-start gap-3 text-sm text-slate-700">
                        <AlertCircle className="h-4 w-4 text-amber-400 mt-0.5 flex-shrink-0" />
                        <span>{factor}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </Section>
        )}

        {/* AI RECOMMENDATION */}
        {structured?.agentRecommendation && (
          <div
            className="rounded-2xl border border-slate-200 bg-white shadow-sm p-6 md:p-8 mb-10 border-l-4"
            style={{ borderLeftColor: primaryColor }}
          >
            <div className="flex items-center gap-3 mb-4">
              <p className="text-sm font-semibold uppercase tracking-[0.2em] text-slate-400">
                AI Recommendation
              </p>
              <span className="flex items-center gap-1.5 text-xs font-medium text-slate-400 border border-slate-200 rounded-full px-2.5 py-1 bg-slate-50">
                <OpenAILogo className="h-3 w-3" />
                Powered by OpenAI
              </span>
            </div>
            <p className="text-slate-700 leading-relaxed">{structured.agentRecommendation}</p>
          </div>
        )}

        {/* DOCUMENTS & LINKS */}
        {documents.length > 0 && (
          <Section title="Documents & Links">
            <div className="space-y-2">
              {documents.map((doc) => {
                const href = doc.link_url ?? doc.public_url;
                const isLink = !!doc.link_url;
                return (
                  <div
                    key={doc.id}
                    className="flex items-center gap-4 rounded-2xl border border-slate-200 bg-white shadow-sm p-4"
                  >
                    <div className="flex-shrink-0 h-10 w-10 rounded-xl bg-slate-100 flex items-center justify-center text-slate-500">
                      {isLink ? <LinkIcon className="h-5 w-5" /> : <FileText className="h-5 w-5" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-900 truncate">{doc.file_name}</p>
                      {doc.file_description && (
                        <p className="text-sm text-slate-400 truncate">{doc.file_description}</p>
                      )}
                    </div>
                    {href && (
                      <a
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex-shrink-0 text-sm font-medium px-4 py-2 rounded-full border border-slate-200 text-slate-600 hover:bg-slate-50 transition-colors"
                      >
                        Open →
                      </a>
                    )}
                  </div>
                );
              })}
            </div>
          </Section>
        )}

        {/* AGENT CTA */}
        {(() => {
          const phone = cma.custom_agent_phone;
          const email = cma.custom_agent_email;
          const footerLogo = cma.custom_footer_logo_url;
          return (
            <div
              className="rounded-2xl p-8 md:p-12 text-center mb-10"
              style={{ backgroundColor: primaryColor }}
            >
              {headshotUrl && (
                <img
                  src={headshotUrl}
                  alt={agentName}
                  className="h-20 w-20 rounded-full object-cover mx-auto mb-5 border-2 border-white/30"
                />
              )}
              {(footerLogo || (!headshotUrl && logoUrl)) && (
                <img
                  src={footerLogo || logoUrl}
                  alt="Logo"
                  className="h-12 object-contain mx-auto mb-5"
                />
              )}
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-2">
                Ready to list? Let's talk.
              </h2>
              <p className="text-white/75 text-base mb-6">
                {agentName}
              </p>
              {(phone || email) && (
                <div className="flex flex-wrap items-center justify-center gap-3">
                  {phone && (
                    <a
                      href={`tel:${phone.replace(/\D/g, "")}`}
                      className="flex items-center gap-2 bg-white/15 hover:bg-white/25 transition-colors border border-white/25 text-white font-medium text-sm px-5 py-2.5 rounded-full"
                    >
                      📞 {phone}
                    </a>
                  )}
                  {email && (
                    <a
                      href={`mailto:${email}`}
                      className="flex items-center gap-2 bg-white/15 hover:bg-white/25 transition-colors border border-white/25 text-white font-medium text-sm px-5 py-2.5 rounded-full"
                    >
                      ✉️ {email}
                    </a>
                  )}
                </div>
              )}
            </div>
          );
        })()}

        {/* Footer */}
        <div className="flex items-center justify-between pt-6 border-t border-slate-100">
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-400">Powered by</span>
            <img src={defaultLogo.defaultLogoUrl} alt="North Reports" className="h-7" />
          </div>
          {cma.published_at && (
            <p className="text-xs text-slate-400">
              {new Date(cma.published_at).toLocaleDateString("en-US", {
                month: "long",
                day: "numeric",
                year: "numeric",
              })}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
